import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Spinner,
  DataTable,
  Button,
  Box,
  Divider,
  Badge,
} from '@shopify/polaris';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { getCoverageAudit, type CoverageAuditResult } from '../api/coverage-audit';

function pct(n: number, total: number): string {
  if (!total) return '0%';
  return `${((n / total) * 100).toFixed(1)}%`;
}

function downloadCsv(filename: string, rows: any[][], header: string[]) {
  const escape = (v: any) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header.join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function CoverageAudit() {
  const [showAllBp, setShowAllBp] = useState(false);
  const audit = useMutation<CoverageAuditResult, Error, void>({ mutationFn: () => getCoverageAudit() });
  const data = audit.data;

  return (
    <Page
      title="Vehicle / wheel coverage audit"
      subtitle="Which vehicles have no storefront-eligible wheel? Eligibility = active + image + total qty ≥ 4 + install_speed ≠ out_of_stock."
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="300" blockAlign="center">
                <Button
                  variant="primary"
                  loading={audit.isPending}
                  disabled={audit.isPending}
                  onClick={() => audit.mutate()}
                >
                  {audit.isPending ? 'Running audit…' : data ? 'Re-run audit' : 'Run audit'}
                </Button>
                {audit.isPending && (
                  <InlineStack gap="200" blockAlign="center">
                    <Spinner size="small" accessibilityLabel="running" />
                    <Text as="span" tone="subdued">
                      Querying ~22k vehicles × fitment_matches — usually 5–30s
                    </Text>
                  </InlineStack>
                )}
                {data && !audit.isPending && (
                  <Text as="span" tone="subdued">
                    Last run{' '}
                    {new Date(data.generatedAt).toLocaleString('en-US', { timeZone: 'America/New_York' })} ·{' '}
                    {(data.elapsedMs / 1000).toFixed(1)}s
                  </Text>
                )}
              </InlineStack>

              <Banner tone="info">
                <Text as="p" variant="bodySm">
                  BP normalization: comparisons use <b>bolt_pattern_canonical</b> (resolved through{' '}
                  <code>bolt_pattern_aliases</code> + <code>bolt_pattern_alias_map</code>), so{' '}
                  <code>5x4.5</code>, <code>5x4.50</code>, and <code>5x114.3</code> count as the same BP.
                  Multi-BP / dual-drilled wheels (<code>bp1</code> + <code>bp2</code>) flow through{' '}
                  <code>wheel_product_bolt_patterns</code> as separate rows, so a wheel that fits two BPs is
                  counted for vehicles on either.
                </Text>
              </Banner>

              {audit.error && (
                <Banner tone="critical" title="Audit failed">
                  <p>{audit.error.message}</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {data && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Headline
                  </Text>
                  <InlineStack gap="600" wrap>
                    <Box>
                      <Text as="p" tone="subdued">
                        Vehicles
                      </Text>
                      <Text as="p" variant="headingLg">
                        {data.headline.vehicles.toLocaleString()}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="p" tone="subdued">
                        Distinct vehicle BPs
                      </Text>
                      <Text as="p" variant="headingLg">
                        {data.headline.vehicleBps.toLocaleString()}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="p" tone="subdued">
                        Wheels total
                      </Text>
                      <Text as="p" variant="headingLg">
                        {data.headline.wheelsTotal.toLocaleString()}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="p" tone="subdued">
                        Wheels eligible
                      </Text>
                      <Text as="p" variant="headingLg">
                        {data.headline.wheelsEligible.toLocaleString()}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="p" tone="subdued">
                        Vehicles w/ any match
                      </Text>
                      <Text as="p" variant="headingLg">
                        {data.headline.vehiclesWithAnyMatch.toLocaleString()}
                      </Text>
                    </Box>
                  </InlineStack>

                  <Divider />

                  <Text as="h2" variant="headingMd">
                    Coverage tiers
                  </Text>
                  <InlineStack gap="600" wrap>
                    <Box>
                      <InlineStack gap="200" blockAlign="center">
                        <Badge tone="success">ok</Badge>
                        <Text as="span" tone="subdued">≥1 eligible wheel</Text>
                      </InlineStack>
                      <Text as="p" variant="headingLg">
                        {data.tiers.ok.toLocaleString()}{' '}
                        <Text as="span" tone="subdued">
                          {pct(data.tiers.ok, data.tiers.total)}
                        </Text>
                      </Text>
                    </Box>
                    <Box>
                      <InlineStack gap="200" blockAlign="center">
                        <Badge tone="warning">tier1</Badge>
                        <Text as="span" tone="subdued">matches exist, none ship</Text>
                      </InlineStack>
                      <Text as="p" variant="headingLg">
                        {data.tiers.tier1.toLocaleString()}{' '}
                        <Text as="span" tone="subdued">
                          {pct(data.tiers.tier1, data.tiers.total)}
                        </Text>
                      </Text>
                    </Box>
                    <Box>
                      <InlineStack gap="200" blockAlign="center">
                        <Badge tone="critical">tier0</Badge>
                        <Text as="span" tone="subdued">no fitment row</Text>
                      </InlineStack>
                      <Text as="p" variant="headingLg">
                        {data.tiers.tier0.toLocaleString()}{' '}
                        <Text as="span" tone="subdued">
                          {pct(data.tiers.tier0, data.tiers.total)}
                        </Text>
                      </Text>
                    </Box>
                  </InlineStack>
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    Multi-BP / dual-drilled coverage
                  </Text>
                  <Text as="p" tone="subdued">
                    Confirms that wheels with two bolt patterns (bp1 + bp2) are participating in the audit.
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'numeric']}
                    headings={['Stat', 'Value']}
                    rows={[
                      ['Wheels total', data.dualDrilledStats.wheels_total.toLocaleString()],
                      ['Wheels with ≥1 BP indexed', data.dualDrilledStats.wheels_with_any_bp.toLocaleString()],
                      ['Wheels dual-drilled (≥2 BPs)', data.dualDrilledStats.wheels_dual_drilled.toLocaleString()],
                      ['Avg BPs per wheel', data.dualDrilledStats.avg_bps_per_wheel.toFixed(2)],
                      ['fitment_matches rows total', data.dualDrilledStats.fitment_matches_total.toLocaleString()],
                    ]}
                  />
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    Tier1 root causes
                  </Text>
                  <Text as="p" tone="subdued">
                    A vehicle can hit multiple causes. {data.tier1Reasons.tier1_total.toLocaleString()} tier1
                    vehicles total.
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'numeric']}
                    headings={['Cause', 'Vehicles affected']}
                    rows={[
                      ['Some matched wheel has no image', data.tier1Reasons.blocked_by_image.toLocaleString()],
                      ['Some matched wheel has total qty < 4', data.tier1Reasons.blocked_by_qty.toLocaleString()],
                      ['Some matched wheel is inactive', data.tier1Reasons.blocked_by_inactive.toLocaleString()],
                      [
                        'Some matched wheel install_speed = out_of_stock',
                        data.tier1Reasons.blocked_by_install_speed.toLocaleString(),
                      ],
                    ]}
                  />
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">
                      By bolt pattern (sorted by uncovered)
                    </Text>
                    <Button
                      onClick={() =>
                        downloadCsv(
                          'coverage_by_bolt_pattern.csv',
                          data.byBp.map((r) => [r.bp, r.vehicles, r.ok, r.tier1, r.tier0]),
                          ['bolt_pattern', 'vehicles', 'ok', 'tier1', 'tier0'],
                        )
                      }
                    >
                      Download CSV
                    </Button>
                  </InlineStack>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
                    headings={['Bolt pattern', 'Vehicles', 'ok', 'tier1', 'tier0']}
                    rows={(showAllBp ? data.byBp : data.byBp.slice(0, 15)).map((r) => [
                      r.bp,
                      r.vehicles.toLocaleString(),
                      r.ok.toLocaleString(),
                      r.tier1.toLocaleString(),
                      r.tier0.toLocaleString(),
                    ])}
                  />
                  {data.byBp.length > 15 && (
                    <Button onClick={() => setShowAllBp((v) => !v)} variant="plain">
                      {showAllBp ? 'Show top 15' : `Show all ${data.byBp.length}`}
                    </Button>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {data.tier0BpCatalog.length > 0 && (
              <Layout.Section>
                <Card>
                  <BlockStack gap="300">
                    <Text as="h2" variant="headingMd">
                      Tier0 BPs (no fitment_matches at all) — catalog presence
                    </Text>
                    <Text as="p" tone="subdued">
                      For each BP that has tier0 vehicles: is there any wheel in the catalog with that BP, and
                      any <i>eligible</i> wheel? "No" in either column = need to source / unblock SKUs for that
                      BP.
                    </Text>
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'text', 'text']}
                      headings={['BP', 'Vehicles affected', 'In catalog', 'Eligible in catalog']}
                      rows={data.tier0BpCatalog.map((r) => [
                        r.bp,
                        r.vehicles_affected.toLocaleString(),
                        r.bp_in_catalog ? 'yes' : 'NO',
                        r.bp_in_eligible_catalog ? 'yes' : 'NO',
                      ])}
                    />
                  </BlockStack>
                </Card>
              </Layout.Section>
            )}

            <Layout.Section>
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">
                      Top makes by uncovered count
                    </Text>
                    <Button
                      onClick={() =>
                        downloadCsv(
                          'coverage_by_make.csv',
                          data.byMake.map((r) => [r.make, r.vehicles, r.uncovered, r.pct_uncovered]),
                          ['make', 'vehicles', 'uncovered', 'pct_uncovered'],
                        )
                      }
                    >
                      Download CSV
                    </Button>
                  </InlineStack>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                    headings={['Make', 'Vehicles', 'Uncovered', '% uncovered']}
                    rows={data.byMake.map((r) => [
                      r.make,
                      r.vehicles.toLocaleString(),
                      r.uncovered.toLocaleString(),
                      `${r.pct_uncovered}%`,
                    ])}
                  />
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    By year
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                    headings={['Year', 'Vehicles', 'Uncovered', '% uncovered']}
                    rows={data.byYear.map((r) => [
                      String(r.year),
                      r.vehicles.toLocaleString(),
                      r.uncovered.toLocaleString(),
                      `${r.pct_uncovered}%`,
                    ])}
                  />
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">
                      Sample uncovered vehicles (50)
                    </Text>
                    <Button
                      onClick={() =>
                        downloadCsv(
                          'coverage_sample_uncovered.csv',
                          data.sample.map((r) => [
                            r.vehicle_id,
                            r.year,
                            r.make_slug,
                            r.model_slug,
                            r.submodel ?? '',
                            r.body_config ?? '',
                            r.bp,
                            r.matches_any,
                            r.matches_eligible,
                            r.tier,
                          ]),
                          [
                            'vehicle_id',
                            'year',
                            'make',
                            'model',
                            'submodel',
                            'body_config',
                            'bp',
                            'matches_any',
                            'matches_eligible',
                            'tier',
                          ],
                        )
                      }
                    >
                      Download CSV
                    </Button>
                  </InlineStack>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'numeric']}
                    headings={['Year', 'Make', 'Model', 'BP', 'Tier', 'Matches', 'Eligible']}
                    rows={data.sample.map((r) => [
                      String(r.year),
                      r.make_slug,
                      r.model_slug,
                      r.bp,
                      r.tier,
                      String(r.matches_any),
                      String(r.matches_eligible),
                    ])}
                  />
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}
